import React, { useContext, useMemo, useState } from 'react';
import BankPaymentReceiptPostMutation from '../../graphql/mutations/bank/BankPaymentReceiptPostMutation.graphql';
import createPostingPreview from '../../views/nominal/NominalPostingPreviewForm';
import BankPaymentReceiptPostingPreview from '../../graphql/entities/bank/BankPaymentReceiptPostingPreview';
import BankPaymentReceiptTransactions from '../../graphql/entities/bank/BankPaymentReceiptTransactions';
import createPostedTransactions from '../../views/nominal/PostedTransactionsForm';
import { PostSelectButton } from '../../components/SelectButton';
import getClientMutationId from '../../utils/getClientMutationId';
import { usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useMutation } from '@apollo/client';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import {
  IBankPaymentReceipt,
  IBankPaymentReceiptPostMutationResult,
  IBankPaymentReceiptPostMutationVariables,
  AlertType,
} from '../../graphql/schema/p4types';
import { BankPaymentReceipt } from '../../graphql/entities';
import { IFormAction } from '../../@types/entityTypes';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import FluentModal from '../../components/modals/FluentModal';

const BankPaymentReviewPostingPreview = createPostingPreview(BankPaymentReceiptPostingPreview);
const BankPaymentReviewPostedTransactions = createPostedTransactions(BankPaymentReceiptTransactions);

enum BankPaymentReceiptAction {
  BankPaymentReceiptPreview = 'BANK_PAYMENT_RECEIPT_PREVIEW_POSTINGS',
  BankPaymentReceiptViewTransactions = 'BANK_PAYMENT_RECEIPT_VIEW_TRANSACTIONS',
  BankPaymentReceiptPost = 'BANK_PAYMENT_RECEIPT_POST_PAYMENT_RECEIPT',
}

export const formActionsForPermissions: Array<IFormAction<BankPaymentReceiptAction>> = [
  {
    actionReference: BankPaymentReceiptAction.BankPaymentReceiptPreview,
  },
  {
    actionReference: BankPaymentReceiptAction.BankPaymentReceiptViewTransactions,
  },
  {
    actionReference: BankPaymentReceiptAction.BankPaymentReceiptPost,
    permissionLabel: 'Bank Payment Receipt Post',
  },
];

export const useBankPaymentReceiptActions = () => {
  const { afterSubmit, match, values } = useContext(ConnectedFormContext);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [postedTransactionsOpen, setPostedTransactionsOpen] = useState(false);
  const [isPosting, setIsPosting] = useState(false);

  const previewPostingsModal = (
    <FluentModal isOpen={previewOpen} onDismiss={() => setPreviewOpen(false)}>
      <BankPaymentReviewPostingPreview match={{ params: { id: values?.id } }} onClose={() => setPreviewOpen(false)} />
    </FluentModal>
  );

  const postedTransactionsModal = (
    <FluentModal isOpen={postedTransactionsOpen} onDismiss={() => setPostedTransactionsOpen(false)}>
      <BankPaymentReviewPostedTransactions
        match={{ params: { id: values?.id } }}
        onClose={() => setPostedTransactionsOpen(false)}
      />
    </FluentModal>
  );

  const [performSave] = usePerformSave();
  const { dispatchSimpleToast } = useP4ToastController();

  const postLabel = useMemo(() => {
    return values &&
      values.bankPaymentReceiptType &&
      (values.bankPaymentReceiptType === 'PAYMENT' || values.bankPaymentReceiptType === 'VATABLE_PAYMENT')
      ? 'Post Payment'
      : 'Post Receipt';
  }, [values]);

  const handleQueryError = useComponentQueryErrorHandling();
  const [postTransactionMutation] = useMutation<
    IBankPaymentReceiptPostMutationResult,
    IBankPaymentReceiptPostMutationVariables
  >(BankPaymentReceiptPostMutation, {
    onCompleted: (data) => {
      if (match?.params?.bank) {
        if (data.bankPaymentReceiptPost?.result) {
          window.opener.postMessage(
            {
              type: 'p4',
              documentType: data.bankPaymentReceiptPost.result.bankPaymentReceiptType,
              id: data.bankPaymentReceiptPost.result.id,
            },
            `${window.location.origin}/Bank/Reconciliation/Details/New`
          );
        }
        window.close();
      }
      afterSubmit?.({ result: data });
    },
    onError: handleQueryError,
    refetchQueries: [BankPaymentReceipt.workspace!],
  });

  return [
    [
      {
        actionReference: BankPaymentReceiptAction.BankPaymentReceiptPreview,
        key: 'previewPostings',
        text: 'Preview Postings',
        onClick: () =>
          performSave({
            onSaveComplete: () => {
              setPreviewOpen(true);
            },
            submitMeta: { suppressAfterSubmit: true },
          }),
        individualButton: true,
        condition: displayPreview,
      },
      {
        actionReference: BankPaymentReceiptAction.BankPaymentReceiptViewTransactions,
        key: 'viewTransactions',
        text: 'View Transactions',
        onClick: () => setPostedTransactionsOpen(true),
        individualButton: true,
        condition: displayPosted,
      },
      {
        actionReference: BankPaymentReceiptAction.BankPaymentReceiptPost,
        key: 'postPaymentReceipt',
        text: postLabel,
        disabled: isPosting,
        onClick: () => {
          setIsPosting(true);
          performSave({
            onSaveComplete: (response) => {
              postTransactionMutation({
                variables: {
                  input: {
                    clientMutationId: getClientMutationId(),
                    id: response?.result?.id,
                  },
                },
                onCompleted: (data) => {
                  if (data?.bankPaymentReceiptPost?.result) {
                    dispatchSimpleToast(
                      'Transaction posted',
                      'Transaction has successfully been posted',
                      AlertType.Success
                    );
                  }
                  afterSubmit?.(response);
                },
              }).finally(() => {
                setIsPosting(false);
              });
            },
            onSaveFailed: () => {
              setIsPosting(false);
            },
            submitMeta: {
              suppressAfterSubmit: true, //Don't process after submit - Post still outstanding.
            },
          });
        },
        individualButton: true,
        individualButtonComponent: PostSelectButton,
        condition: canBePosted,
      },
    ],
    <>
      {previewPostingsModal}
      {postedTransactionsModal}
    </>,
  ];
};

const displayPreview = (values: IBankPaymentReceipt) => {
  if (values && values.documentStatus) {
    return values.documentStatus === 'DRAFT' || values.documentStatus === 'RELEASED';
  }
  return false;
};

const displayPosted = (values: IBankPaymentReceipt) => {
  if (values && values.documentStatus) {
    return values.documentStatus === 'POSTED';
  }
  return false;
};

const canBePosted = (values: IBankPaymentReceipt) => {
  if (values && values.documentStatus) {
    return values.documentStatus === 'DRAFT';
  }
  return false;
};
